/*
------------------------------------------------------------------------
Nonce Refresh Utility

Provides proactive nonce refresh on window focus to prevent 403 errors
when users return to an idle browser tab.
------------------------------------------------------------------------
*/

let isRefreshing = false;
let refreshPromise = null;

/*
------------------------------------------------------------------------
Refresh the WordPress nonce by calling the refresh endpoint.

@return {Promise<string|null>} The new nonce, or null if refresh failed.
------------------------------------------------------------------------
*/
export async function refreshNonce() {
    // Prevent concurrent refresh requests
    if (isRefreshing && refreshPromise) {
        return refreshPromise;
    }

    isRefreshing = true;
    refreshPromise = (async () => {
        try {
            const response = await fetch('/wp-json/lat/v1/refresh-nonce', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                return null;
            }

            const result = await response.json();
            if (result.nonce && window.wpData) {
                window.wpData.nonce = result.nonce;
                return result.nonce;
            }
            return null;
        } catch (error) {
            return null;
        } finally {
            isRefreshing = false;
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}

/*
------------------------------------------------------------------------
Set up proactive nonce refresh on window focus.

When the user returns to the tab after being away, refresh the nonce
to prevent 403 errors on the next API call.
------------------------------------------------------------------------
*/
export function setupNonceRefreshOnFocus() {
    let lastRefreshTime = Date.now();
    const MIN_REFRESH_INTERVAL = 60000; // Don't refresh more than once per minute

    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            const now = Date.now();
            // Only refresh if enough time has passed since last refresh
            if (now - lastRefreshTime >= MIN_REFRESH_INTERVAL) {
                lastRefreshTime = now;
                refreshNonce().then((nonce) => {
                    if (nonce) {
                        $log.log('[nonce:handleVisibilityChange] Nonce refreshed on tab focus');
                    }
                });
            }
        }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Return cleanup function
    return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
}
